import { useState, useEffect } from "react";
import { format, parseISO, startOfMonth, endOfMonth } from "date-fns";
import { Plus, Banknote, FileText, ClipboardList, Trash2, Edit2, Minus, Save, MapPin, ImagePlus, Video, X } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useBusiness } from "@/hooks/use-business";
import { useLocations } from "@/hooks/use-locations";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

type LogBookEntryType = "activity" | "expense" | "note";

interface ExpenseLineItem {
  item: string;
  amount: number;
}

export type LogBookMediaType = "image" | "video";

interface LogBookMediaItem {
  url: string;
  type: LogBookMediaType;
}

interface LogBookEntry {
  id: string;
  business_id: string;
  location_id: string | null;
  entry_date: string;
  entry_type: LogBookEntryType;
  title: string | null;
  description: string | null;
  amount: number | null;
  currency: string | null;
  line_items: ExpenseLineItem[] | null;
  media: LogBookMediaItem[] | null;
  appointment_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export default function LogBook() {
  const { business, loading: businessLoading } = useBusiness();
  const { locations } = useLocations();
  const { toast } = useToast();
  
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [datesWithEntries, setDatesWithEntries] = useState<Date[]>([]);
  const [entries, setEntries] = useState<LogBookEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<LogBookEntry | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);
  
  // Form state
  const [entryType, setEntryType] = useState<LogBookEntryType>("note");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [locationId, setLocationId] = useState<string>("");
  const [lineItems, setLineItems] = useState<ExpenseLineItem[]>([{ item: "", amount: 0 }]);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [attachedPreviewUrls, setAttachedPreviewUrls] = useState<string[]>([]);
  const [existingMedia, setExistingMedia] = useState<LogBookMediaItem[]>([]);
  const [mediaToRemove, setMediaToRemove] = useState<string[]>([]);
  
  // Check if log book is enabled
  const logBookEnabled = (business as any)?.log_book_enabled ?? false;
  const currency = (business as any)?.currency || "GHS";

  // Load entries for selected date
  useEffect(() => {
    if (!business || !logBookEnabled) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const loadEntries = async () => {
      try {
        if (isMounted) setLoading(true);
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        
        const { data, error } = await supabase
          .from("log_book_entries")
          .select("*")
          .eq("business_id", business.id)
          .eq("entry_date", dateStr)
          .order("created_at", { ascending: false });

        if (!isMounted) return;

        if (error) throw error;
        setEntries((data || []) as LogBookEntry[]);
      } catch (error: any) {
        if (!isMounted) return;
        toast({
          title: "Error",
          description: error.message || "Failed to load log book entries",
          variant: "destructive",
        });
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadEntries();

    return () => {
      isMounted = false;
    };
  }, [business, selectedDate, logBookEnabled, toast]);

  // Fetch all distinct entry dates for the visible calendar month to render dots.
  useEffect(() => {
    if (!business || !logBookEnabled) return;

    const start = format(startOfMonth(calendarMonth), "yyyy-MM-dd");
    const end = format(endOfMonth(calendarMonth), "yyyy-MM-dd");

    supabase
      .from("log_book_entries")
      .select("entry_date")
      .eq("business_id", business.id)
      .gte("entry_date", start)
      .lte("entry_date", end)
      .then(({ data }) => {
        if (!data) return;
        const unique = [...new Set(data.map((e) => e.entry_date))];
        setDatesWithEntries(unique.map((d) => parseISO(d)));
      });
  }, [business, logBookEnabled, calendarMonth]);

  const handleOpenDialog = (type: LogBookEntryType, entry?: LogBookEntry) => {
    setAttachedFiles([]);
    setAttachedPreviewUrls([]);
    setExistingMedia([]);
    setMediaToRemove([]);
    if (entry) {
      setEditingEntry(entry);
      setEntryType(entry.entry_type);
      if (entry.entry_type === "expense") {
        if (entry.line_items && Array.isArray(entry.line_items) && entry.line_items.length > 0) {
          setLineItems(entry.line_items);
        } else if (entry.amount && entry.title) {
          setLineItems([{ item: entry.title, amount: entry.amount }]);
        } else {
          setLineItems([{ item: "", amount: 0 }]);
        }
        setTitle("");
      } else {
        setTitle(entry.title || "");
        setLineItems([{ item: "", amount: 0 }]);
      }
      setDescription(entry.description || "");
      setLocationId(entry.location_id || "");
      const mediaList = entry.media && Array.isArray(entry.media) ? entry.media : [];
      setExistingMedia(mediaList);
    } else {
      setEditingEntry(null);
      setEntryType(type);
      setTitle("");
      setDescription("");
      setLocationId("");
      setLineItems([{ item: "", amount: 0 }]);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEntry(null);
    setTitle("");
    setDescription("");
    setLocationId("");
    setLineItems([{ item: "", amount: 0 }]);
    setAttachedFiles([]);
    attachedPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    setAttachedPreviewUrls([]);
    setExistingMedia([]);
    setMediaToRemove([]);
  };

  const getStoragePathFromPublicUrl = (url: string): string | null => {
    try {
      const match = url.match(/\/storage\/v1\/object\/public\/service-media\/(.+)$/);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  };

  const uploadLogBookMedia = async (businessId: string, entryId: string, files: File[]): Promise<LogBookMediaItem[]> => {
    const bucket = "service-media";
    const prefix = `log-book/${businessId}/${entryId}`;
    const results: LogBookMediaItem[] = [];
    for (const file of files) {
      const ext = file.name.split(".").pop() || (file.type.startsWith("video/") ? "mp4" : "jpg");
      const path = `${prefix}/${crypto.randomUUID()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from(bucket).upload(path, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: file.type || (file.type.startsWith("video/") ? "video/mp4" : "image/jpeg"),
      });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
      const type: LogBookMediaType = file.type.startsWith("video/") ? "video" : "image";
      results.push({ url: urlData.publicUrl, type });
    }
    return results;
  };

  const handleSaveEntry = async () => {
    if (!business) {
      toast({
        title: "Error",
        description: "Business not found",
        variant: "destructive",
      });
      return;
    }

    // Validation
    if (entryType === "expense") {
      const validLineItems = lineItems.filter(li => li.item.trim() && li.amount > 0);
      if (validLineItems.length === 0) {
        toast({
          title: "Error",
          description: "Please add at least one line item with item name and amount",
          variant: "destructive",
        });
        return;
      }
    } else {
      if (!title.trim()) {
        toast({
          title: "Error",
          description: "Title is required",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      
      let totalAmount = 0;
      let finalLineItems: ExpenseLineItem[] | null = null;
      
      if (entryType === "expense") {
        finalLineItems = lineItems.filter(li => li.item.trim() && li.amount > 0);
        totalAmount = finalLineItems.reduce((sum, li) => sum + li.amount, 0);
      }

      const keptExisting = existingMedia.filter((m) => !mediaToRemove.includes(m.url));

      let entryId: string;

      if (editingEntry) {
        entryId = editingEntry.id;
        const newMedia = attachedFiles.length > 0 ? await uploadLogBookMedia(business.id, entryId, attachedFiles) : [];
        const finalMedia = [...keptExisting, ...newMedia];

        const entryData: any = {
          business_id: business.id,
          entry_date: dateStr,
          entry_type: entryType,
          title: entryType === "expense" ? null : title.trim(),
          description: description.trim() || null,
          location_id: locationId || null,
          currency: entryType === "expense" ? currency : null,
          amount: entryType === "expense" ? totalAmount : null,
          line_items: entryType === "expense" ? finalLineItems : null,
          media: finalMedia,
        };

        const { error } = await supabase
          .from("log_book_entries")
          .update(entryData)
          .eq("id", entryId);

        if (error) throw error;

        for (const url of mediaToRemove) {
          const path = getStoragePathFromPublicUrl(url);
          if (path) {
            try {
              await supabase.storage.from("service-media").remove([path]);
            } catch {
              // ignore
            }
          }
        }

        toast({
          title: "Success",
          description: "Entry updated successfully",
        });
      } else {
        const entryData: any = {
          business_id: business.id,
          entry_date: dateStr,
          entry_type: entryType,
          title: entryType === "expense" ? null : title.trim(),
          description: description.trim() || null,
          location_id: locationId || null,
          currency: entryType === "expense" ? currency : null,
          amount: entryType === "expense" ? totalAmount : null,
          line_items: entryType === "expense" ? finalLineItems : null,
          media: [],
        };

        const { data: inserted, error: insertError } = await supabase
          .from("log_book_entries")
          .insert(entryData)
          .select("id")
          .single();

        if (insertError) throw insertError;
        entryId = inserted.id;

        if (attachedFiles.length > 0) {
          const newMedia = await uploadLogBookMedia(business.id, entryId, attachedFiles);
          const { error: updateError } = await supabase
            .from("log_book_entries")
            .update({ media: newMedia })
            .eq("id", entryId);
          if (updateError) throw updateError;
        }

        toast({
          title: "Success",
          description: "Entry added successfully",
        });
      }

      handleCloseDialog();
      
      const { data, error: reloadError } = await supabase
        .from("log_book_entries")
        .select("*")
        .eq("business_id", business.id)
        .eq("entry_date", dateStr)
        .order("created_at", { ascending: false });

      if (!reloadError && data) {
        setEntries(data);
      }

      // Refresh calendar dots for the visible month.
      const start = format(startOfMonth(calendarMonth), "yyyy-MM-dd");
      const end = format(endOfMonth(calendarMonth), "yyyy-MM-dd");
      supabase
        .from("log_book_entries")
        .select("entry_date")
        .eq("business_id", business.id)
        .gte("entry_date", start)
        .lte("entry_date", end)
        .then(({ data: dotData }) => {
          if (!dotData) return;
          const unique = [...new Set(dotData.map((e) => e.entry_date))];
          setDatesWithEntries(unique.map((d) => parseISO(d)));
        });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save entry",
        variant: "destructive",
      });
    }
  };

  const handleDeleteEntry = async () => {
    if (!entryToDelete || !business) return;

    try {
      const entry = entries.find((e) => e.id === entryToDelete);
      if (entry?.media && Array.isArray(entry.media) && entry.media.length > 0) {
        for (const m of entry.media) {
          const path = getStoragePathFromPublicUrl(m.url);
          if (path) {
            try {
              await supabase.storage.from("service-media").remove([path]);
            } catch {
              // ignore
            }
          }
        }
      }

      const { error } = await supabase
        .from("log_book_entries")
        .delete()
        .eq("id", entryToDelete);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Entry deleted successfully",
      });

      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const { data, error: reloadError } = await supabase
        .from("log_book_entries")
        .select("*")
        .eq("business_id", business.id)
        .eq("entry_date", dateStr)
        .order("created_at", { ascending: false });

      if (!reloadError && data) {
        setEntries(data);
      }

      setDeleteDialogOpen(false);
      setEntryToDelete(null);

      // Refresh calendar dots after deletion.
      const start = format(startOfMonth(calendarMonth), "yyyy-MM-dd");
      const end = format(endOfMonth(calendarMonth), "yyyy-MM-dd");
      supabase
        .from("log_book_entries")
        .select("entry_date")
        .eq("business_id", business.id)
        .gte("entry_date", start)
        .lte("entry_date", end)
        .then(({ data: dotData }) => {
          if (!dotData) return;
          const unique = [...new Set(dotData.map((e) => e.entry_date))];
          setDatesWithEntries(unique.map((d) => parseISO(d)));
        });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete entry",
        variant: "destructive",
      });
    }
  };

  const getEntryIcon = (type: LogBookEntryType) => {
    switch (type) {
      case "activity":
        return <ClipboardList className="h-5 w-5" />;
      case "expense":
        return <Banknote className="h-5 w-5" />;
      case "note":
        return <FileText className="h-5 w-5" />;
    }
  };

  const getLocationName = (locationId: string | null) => {
    if (!locationId) return null;
    const location = locations.find((l) => l.id === locationId);
    return location?.name || "Unknown";
  };

  if (businessLoading) {
    return (
      <AppShell businessName={business?.name || "My Business"}>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </AppShell>
    );
  }

  if (!logBookEnabled) {
    return (
      <AppShell businessName={business?.name || "My Business"}>
        <PageHeader title="Log Book" />
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6">
          <Card>
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-sm sm:text-base text-muted-foreground mb-2">
                Log Book is not enabled for your business.
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Enable it in Settings to start logging daily activities, expenses, and notes.
              </p>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader 
        title="Log Book" 
        description="Track daily activities, expenses, and notes for your business"
        action={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="pill">
                <Plus className="mr-2 h-4 w-4" />
                Add Log
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleOpenDialog("activity")}>
                <ClipboardList className="mr-2 h-4 w-4" />
                Add Activity
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleOpenDialog("expense")}>
                <Banknote className="mr-2 h-4 w-4" />
                Add Expense
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleOpenDialog("note")}>
                <FileText className="mr-2 h-4 w-4" />
                Add Note
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Calendar */}
        <Card>
          <CardHeader className="p-3 sm:p-4">
            <CardTitle className="text-base sm:text-lg">Select Date</CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              modifiers={{ hasEntry: datesWithEntries }}
              modifiersClassNames={{ hasEntry: "day-has-entry" }}
              className="w-full max-w-full rounded-md border-0 [&_.rdp]:w-full [&_.rdp-months]:w-full [&_.rdp-month]:w-full [&_.rdp-table]:w-full [&_table]:w-full [&_thead]:w-full [&_tbody]:w-full [&_tr]:grid [&_tr]:grid-cols-7 [&_tr]:w-full [&_td]:flex [&_td]:justify-center [&_th]:flex [&_th]:justify-center [&_.rdp-cell]:flex-1 [&_.rdp-day]:h-10 [&_.rdp-day]:w-10"
            />
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs sm:text-sm text-muted-foreground">
                Selected: {format(selectedDate, "EEEE, MMMM d, yyyy")}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Entries for Selected Date */}
        <Card key={format(selectedDate, "yyyy-MM-dd")} className="w-full">
          <CardHeader className="p-3 sm:p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base sm:text-lg">
                Entries for {format(selectedDate, "MMMM d, yyyy")}
              </CardTitle>
              <Badge variant="secondary" className="text-xs">{entries.length} entries</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            {loading ? (
              <div className="text-center py-8 text-sm text-muted-foreground">Loading...</div>
            ) : entries.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm sm:text-base text-muted-foreground mb-2">
                  No entries for this date.
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Click "Add Log" to create one.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {entries.map((entry) => {
                  const locationName = getLocationName(entry.location_id);
                  
                  return (
                    <Card key={entry.id} className="overflow-hidden">
                      <CardContent className="p-0">
                        {/* Entry Header */}
                        <div className={cn(
                          "px-3 py-2.5 border-b",
                          entry.entry_type === "expense" ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900" :
                          entry.entry_type === "activity" ? "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900" :
                          "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900"
                        )}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={cn(
                                "p-2 rounded-lg",
                                entry.entry_type === "expense" ? "bg-red-100 dark:bg-red-900/30" :
                                entry.entry_type === "activity" ? "bg-blue-100 dark:bg-blue-900/30" :
                                "bg-green-100 dark:bg-green-900/30"
                              )}>
                                {getEntryIcon(entry.entry_type)}
                              </div>
                              <div>
                                <Badge variant="outline" className="text-xs capitalize">
                                  {entry.entry_type}
                                </Badge>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {format(parseISO(entry.created_at), "h:mm a")}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleOpenDialog(entry.entry_type, entry)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => {
                                setEntryToDelete(entry.id);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            </div>
                          </div>
                        </div>

                        {/* Entry Content */}
                        <div className="p-3 space-y-3">
                          {entry.entry_type === "expense" && entry.line_items && Array.isArray(entry.line_items) && entry.line_items.length > 0 ? (
                            <>
                              <div className="space-y-2">
                                {entry.line_items.map((li: ExpenseLineItem, idx: number) => (
                                  <div key={idx} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                                    <span className="text-sm text-foreground flex-1">{li.item}</span>
                                    <span className="text-sm font-semibold text-foreground ml-4">
                                      {currency} {Number(li.amount).toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                              <div className="pt-2 border-t-2 border-border">
                                <div className="flex items-center justify-between">
                                  <span className="text-base font-semibold text-foreground">Total</span>
                                  <span className="text-lg font-bold text-foreground">
                                    {currency} {Number(entry.amount || 0).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </>
                          ) : entry.entry_type === "expense" && entry.title ? (
                            <div>
                              <h3 className="font-semibold text-base mb-2">{entry.title}</h3>
                              {entry.amount && (
                                <p className="text-lg font-bold">
                                  {currency} {Number(entry.amount).toFixed(2)}
                                </p>
                              )}
                            </div>
                          ) : (
                            <div>
                              <h3 className="font-semibold text-base mb-2">{entry.title || "Untitled"}</h3>
                            </div>
                          )}

                          {entry.description && (
                            <div className="pt-2 border-t border-border/50">
                              <p className="text-sm text-muted-foreground leading-relaxed">
                                {entry.description}
                              </p>
                            </div>
                          )}

                          {locationName && (
                            <div className="pt-2 border-t border-border/50">
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <MapPin className="h-3 w-3" />
                                <span>{locationName}</span>
                              </div>
                            </div>
                          )}

                          {entry.media && Array.isArray(entry.media) && entry.media.length > 0 && (
                            <div className="pt-2 border-t border-border/50">
                              <div className="flex flex-wrap gap-2">
                                {entry.media.map((m, idx) =>
                                  m.type === "image" ? (
                                    <a
                                      key={idx}
                                      href={m.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block rounded overflow-hidden border border-border hover:opacity-90"
                                    >
                                      <img src={m.url} alt="" className="h-20 w-20 object-cover" />
                                    </a>
                                  ) : (
                                    <div key={idx} className="rounded overflow-hidden border border-border">
                                      <video
                                        src={m.url}
                                        controls
                                        className="h-24 w-40 object-cover"
                                        playsInline
                                        preload="metadata"
                                      />
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">
              {editingEntry ? "Edit Entry" : `Add ${entryType.charAt(0).toUpperCase() + entryType.slice(1)}`}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {editingEntry
                ? "Update the entry details below"
                : `Add a new ${entryType} entry for ${format(selectedDate, "MMMM d, yyyy")}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm">Type</Label>
              <Select 
                value={entryType} 
                onValueChange={(v) => {
                  setEntryType(v as LogBookEntryType);
                  if (v === "expense") {
                    setTitle("");
                    setLineItems([{ item: "", amount: 0 }]);
                  } else {
                    setLineItems([{ item: "", amount: 0 }]);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activity">Activity</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="note">Note</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {entryType !== "expense" && (
              <div className="space-y-2">
                <Label className="text-sm">Title *</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter title"
                  className="text-sm"
                />
              </div>
            )}

            {entryType === "expense" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Line Items *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setLineItems([...lineItems, { item: "", amount: 0 }])}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Item
                  </Button>
                </div>
                <div className="space-y-2">
                  {lineItems.map((lineItem, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <div className="flex-1 space-y-1">
                        <Input
                          value={lineItem.item}
                          onChange={(e) => {
                            const updated = [...lineItems];
                            updated[index].item = e.target.value;
                            setLineItems(updated);
                          }}
                          placeholder="Item name"
                          className="text-sm"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={lineItem.amount || ""}
                          onChange={(e) => {
                            const updated = [...lineItems];
                            updated[index].amount = Number(e.target.value) || 0;
                            setLineItems(updated);
                          }}
                          placeholder="0.00"
                          className="text-sm"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => {
                          if (lineItems.length > 1) {
                            setLineItems(lineItems.filter((_, i) => i !== index));
                          }
                        }}
                        disabled={lineItems.length === 1}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                {lineItems.some(li => li.item.trim() && li.amount > 0) && (
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold">Total:</span>
                      <span className="font-semibold">
                        {currency} {lineItems
                          .filter(li => li.item.trim() && li.amount > 0)
                          .reduce((sum, li) => sum + li.amount, 0)
                          .toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description (optional)"
                rows={3}
                className="text-sm"
              />
            </div>

            {locations.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">Location (Optional)</Label>
                <Select value={locationId || "none"} onValueChange={(value) => setLocationId(value === "none" ? "" : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm">Media (Optional)</Label>
              <p className="text-xs text-muted-foreground">Add photos or videos to this entry.</p>
              <div className="flex flex-wrap gap-2 items-center">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    className="sr-only"
                    onChange={(e) => {
                      const files = e.target.files ? Array.from(e.target.files) : [];
                      const urls = files.map((f) => URL.createObjectURL(f));
                      setAttachedFiles((prev) => [...prev, ...files]);
                      setAttachedPreviewUrls((prev) => [...prev, ...urls]);
                      e.target.value = "";
                    }}
                  />
                  <Button type="button" variant="outline" size="sm" className="text-xs" asChild>
                    <span>
                      <ImagePlus className="h-4 w-4 mr-1 inline" />
                      Add photos / video
                    </span>
                  </Button>
                </label>
              </div>
              {(existingMedia.length > 0 || attachedFiles.length > 0) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {existingMedia.map((m) => (
                    <div key={m.url} className="relative group">
                      {mediaToRemove.includes(m.url) ? (
                        <div className="w-20 h-20 rounded border border-dashed border-muted-foreground/50 flex items-center justify-center text-xs text-muted-foreground">
                          Removed
                        </div>
                      ) : m.type === "image" ? (
                        <img src={m.url} alt="" className="w-20 h-20 object-cover rounded border" />
                      ) : (
                        <video src={m.url} className="w-20 h-20 object-cover rounded border" muted playsInline />
                      )}
                      {!mediaToRemove.includes(m.url) && (
                        <button
                          type="button"
                          onClick={() => setMediaToRemove((prev) => [...prev, m.url])}
                          className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-90 hover:opacity-100"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                  {attachedFiles.map((file, idx) => (
                    <div key={`${file.name}-${idx}`} className="relative group">
                      {file.type.startsWith("video/") ? (
                        <div className="w-20 h-20 rounded border border-muted flex items-center justify-center text-xs text-muted-foreground">
                          <Video className="h-6 w-6" />
                        </div>
                      ) : (
                        <img src={attachedPreviewUrls[idx]} alt="" className="w-20 h-20 object-cover rounded border" />
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          URL.revokeObjectURL(attachedPreviewUrls[idx]);
                          setAttachedPreviewUrls((prev) => prev.filter((_, i) => i !== idx));
                          setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));
                        }}
                        className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-90 hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleCloseDialog} className="w-full sm:w-auto text-sm">
                Cancel
              </Button>
              <Button onClick={handleSaveEntry} className="w-full sm:w-auto text-sm">
                <Save className="mr-2 h-4 w-4" />
                {editingEntry ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Entry"
        description="Are you sure you want to delete this entry? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleDeleteEntry}
      />
    </AppShell>
  );
}
